import React from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import Button from '../../../../library/button/button';
import Typography from '../../../../library/typography/typography';
import {
  CheckCircleSolidIcon,
  ExclamationTriangleSolidIcon,
  TimesCircleSolidIcon
} from '../../../../shared/icons/icons';

import {
  useNavigateTo,
  useProductSearchNavigation
} from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics.js';
import { ProductTypeProp } from '../../../../shared/utils/prop-types/prop-types';

import { PRODUCT_TYPES } from '../../../../shared/constants/products';
import { SEARCH_TYPES } from '../../../../shared/constants/search-types';

import './fit-message.scss';

function FitMessage(props) {
  const {
    doesFit = false,
    productType = PRODUCT_TYPES.TIRES,
    isLoading,
    isPackage,
    fitment,
    isTextSearch,
    isSingular
  } = props;

  const location = useLocation();
  const navigateTo = useNavigateTo();
  const { onSearchTypeSelected, onShopAllAccessories, onShopAllTires } =
    useProductSearchNavigation({
      fitment
    });

  let containerStyleName = 'container';
  let productMessage = 'tires';
  let fitmentActionMessage = '';
  let Icon = null;
  let iconStyleName = null;
  let message = '';
  let fitmentAction = null;

  function handleEnterYourVehicleClick() {
    const locationHash = '#/fitment/vehicle/continue-to/close';
    const url = new URL(window.location);
    trackAction('product_fitment_validation_banner');
    if (Number(url.searchParams.get('page')) !== 0) {
      url.hash = locationHash;
      url.searchParams.set('page', 0);
      window.location.assign(url);
    } else {
      window.location.hash = locationHash;
    }
  }

  function handleShopProductsThatFitClick() {
    trackAction('product_fitment_validation');
    if (productType === PRODUCT_TYPES.TIRES) {
      onShopAllTires();
    } else if (productType === PRODUCT_TYPES.ACCESSORIES) {
      onShopAllAccessories();
    } else {
      onSearchTypeSelected(SEARCH_TYPES.WHEELS);
    }
  }

  if (PRODUCT_TYPES[productType] === PRODUCT_TYPES.ACCESSORIES) {
    productMessage = 'products';
  } else if (PRODUCT_TYPES[productType] === PRODUCT_TYPES.WHEELS) {
    productMessage = 'wheels';
  }

  if (!fitment) {
    containerStyleName += ' container--warning';
    fitmentActionMessage = 'Enter your vehicle';
    fitmentAction = handleEnterYourVehicleClick;
    Icon = ExclamationTriangleSolidIcon;
    iconStyleName = 'icon-exclamation-triangle';
    message = `to ensure these ${productMessage} fit`;
  }

  if (!isLoading && fitment) {
    if (doesFit || isPackage) {
      containerStyleName += ' container--does-fit';
      fitmentAction = null;
      fitmentActionMessage = null;
      Icon = CheckCircleSolidIcon;
      iconStyleName = 'icon-check-circle';
      message = isPackage
        ? 'This package fits your vehicle'
        : `${
            isSingular ? 'This fits' : `These ${productMessage} fit`
          } your vehicle`;
      if (isTextSearch) {
        fitmentAction = () => {
          navigateTo(`${location.pathname}${location.search}&isSearchAll=true`);
        };
        fitmentActionMessage = 'view all results for this search';
      } else {
        fitmentAction = null;
        fitmentActionMessage = null;
      }
    } else if (
      process.env.__FF_TIRES_THAT_DONT_FIT_MESSAGING__ === 'true' &&
      productType === PRODUCT_TYPES.TIRES &&
      !doesFit
    ) {
      containerStyleName += ' container--warning';
      Icon = ExclamationTriangleSolidIcon;
      iconStyleName = 'icon-exclamation-triangle';
      message = 'These tires may not fit your vehicle.';
    } else {
      containerStyleName += ' container--does-not-fit';
      fitmentAction = handleShopProductsThatFitClick;
      fitmentActionMessage = `shop ${productMessage} that fit`;
      Icon = TimesCircleSolidIcon;
      iconStyleName = 'icon-times-circle';
      message = `${
        isSingular
          ? `This ${productMessage} does not`
          : `These ${productMessage} don't`
      } fit your vehicle`;
    }
  }

  return (
    <div className="fit-message" styleName={containerStyleName}>
      <span styleName="message-container">
        {iconStyleName && <Icon styleName={iconStyleName} />}
        {message && (
          <Typography
            aria-live="polite"
            size="small"
            styleName="message"
            tag="span"
            weight="heavy"
          >
            {!fitment && fitmentAction && (
              <Button onClick={fitmentAction} weight="heavy">
                {fitmentActionMessage}
              </Button>
            )}{' '}
            {message}
            {fitment && fitmentAction && (
              <Button onClick={fitmentAction} styleName="button">
                {fitmentActionMessage}
              </Button>
            )}
          </Typography>
        )}
      </span>
    </div>
  );
}

FitMessage.propTypes = {
  doesFit: PropTypes.bool.isRequired,
  fitment: PropTypes.any,
  isLoading: PropTypes.bool,
  isPackage: PropTypes.bool,
  isSingular: PropTypes.bool,
  isTextSearch: PropTypes.bool,
  isVehicle: PropTypes.bool,
  productType: ProductTypeProp
};

export default FitMessage;
